import React from "react";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";

interface ServiceImage {
  id?: number;
  file: File | string;
  isDefault: boolean;
}

interface MultiImageUploadProps {
  indexId: string | number; // Unique tracking identifier (e.g., row index)
  images: ServiceImage[];
  onChange: (updatedImages: ServiceImage[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  acceptedTypes?: string;
}

export default function MultiImageUpload({
  indexId,
  images,
  onChange,
  maxFiles = 3,
  maxSizeMB = 10,
  acceptedTypes = "image/*"
}: MultiImageUploadProps) {

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    
    const incomingFiles = Array.from(e.target.files);
    
    // Validation 1: Hard Max File Counter check
    if (images.length + incomingFiles.length > maxFiles) {
      Swal.fire("Limit Reached", `You can only upload a maximum of ${maxFiles} images per item.`, "warning");
      e.target.value = ""; // Reset file picker channel
      return;
    }

    const validatedNewImages: ServiceImage[] = [];
    const bytesLimit = maxSizeMB * 1024 * 1024;

    for (const file of incomingFiles) {
      // Validation 2: Max File Size constraints check
      if (file.size > bytesLimit) {
        Swal.fire("File Too Large", `"${file.name}" exceeds the ${maxSizeMB}MB size limit.`, "error");
        continue;
      }
      
      validatedNewImages.push({
        file: file,
        isDefault: false // Evaluated cleanly next
      });
    }

    if (validatedNewImages.length === 0) return;

    const combinedImages = [...images, ...validatedNewImages];

    // Guarantee rule: If absolutely no default option is checked, set the very first file as default
    const hasDefaultAlready = combinedImages.some(img => img.isDefault);
    if (!hasDefaultAlready && combinedImages.length > 0) {
      combinedImages[0].isDefault = true;
    }

    onChange(combinedImages);
    e.target.value = ""; // Reset input buffer
  };

  const toggleDefaultFlag = (targetIdx: number) => {
    const updated = images.map((img, idx) => ({
      ...img,
      isDefault: idx === targetIdx
    }));
    onChange(updated);
  };

  const handleRemoveImage = (targetIdx: number) => {
    const targetImage = images[targetIdx];

    // Validation 3: Block deletion if the image is currently selected as primary default
    if (targetImage.isDefault) {
      Swal.fire({
        icon: "error",
        title: "Action Denied",
        text: "You cannot delete the primary default image! Please mark another image as default first.",
        confirmButtonColor: "#212529"
      });
      return;
    }

    const updated = images.filter((_, idx) => idx !== targetIdx);
    onChange(updated);
  };

  return (
    <div className="d-flex align-items-start gap-2">
      {/* Action Activation Sector */}
      <div className="d-flex flex-column" style={{ minWidth: "120px" }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 py-2"
          disabled={images.length >= maxFiles}
          onClick={() => document.getElementById(`upload_picker_${indexId}`)?.click()}
        >
          <FeatherIcon icon="upload" size={14} />
          <span>Add Images</span>
        </button>
        <span className="text-muted text-center" style={{ fontSize: "10px", marginTop: "3px" }}>
          ({images.length}/{maxFiles} allowed)
        </span>

        <input
          type="file"
          id={`upload_picker_${indexId}`}
          accept={acceptedTypes}
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      {/* Structured Thumbnail Gallery Layer */}
      <div className="d-flex flex-wrap gap-2 align-items-center border p-1 rounded bg-light flex-grow-1" style={{ minHeight: "52px" }}>
        {images.length === 0 ? (
          <span className="text-muted small ps-2 py-1" style={{ fontSize: "12px" }}>No media uploaded</span>
        ) : (
          images.map((img, imgIdx) => {
            const imagePreviewUrl = img.file instanceof File ? URL.createObjectURL(img.file) : img.file;
            return (
              <div 
                key={imgIdx} 
                className={`position-relative border p-1 bg-white rounded text-center d-flex flex-column align-items-center transition-all ${img.isDefault ? 'border-primary shadow-sm' : ''}`}
                style={{ width: "54px" }}
              >
                <img
                  src={imagePreviewUrl}
                  className="rounded"
                  style={{ width: "44px", height: "44px", objectFit: "cover" }}
                  alt="preview"
                />

                {/* Custom Radio Button Layout wrapper */}
                <div className="mt-1 d-flex align-items-center justify-content-center">
                  <input
                    type="radio"
                    name={`default_radio_pool_${indexId}`}
                    checked={img.isDefault}
                    onChange={() => toggleDefaultFlag(imgIdx)}
                    className="form-check-input cursor-pointer m-0"
                    style={{ width: "12px", height: "12px" }}
                    title={img.isDefault ? "Primary Default Selection" : "Set as primary view photo"}
                  />
                </div>

                {/* Secure Close Action Trigger */}
                <span
                  className="position-absolute badge bg-danger rounded-circle d-flex align-items-center justify-content-center cursor-pointer p-0 shadow-sm"
                  style={{ 
                    top: "-5px", 
                    right: "-5px", 
                    width: "14px", 
                    height: "14px", 
                    fontSize: "8px",
                    lineHeight: 1 
                  }}
                  onClick={() => handleRemoveImage(imgIdx)}
                  title="Remove Asset"
                >
                  ✕
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}